<template>
  <SheetModal
    class="FeedbackModalView"
    data-theme="light"
    data-testid="feedbackModal"
    fit="hug"
    aria-label="Send feedback"
    :aria-busy="isRequesting"
    @close="$emit('close')"
  >
    <form
      class="FeedbackModalView__form"
      @submit.prevent="handleSubmit"
    >
      <fieldset
        class="FeedbackModalView__fieldset"
        :disabled="isRequesting"
      >
        <SheetHeader
          title="Send feedback"
          submitLabel="Submit"
          :isSubmitDisabled="!trimmedMessage"
          @cancel="$emit('close')"
        />

        <div class="FeedbackModalView__fieldGrid">
          <FormInput
            v-model="form.message"
            name="message"
            :placeholder="`Feature request, bug report,\nyou the real mvp, etc.`"
            :rows="8"
            isMultiline
            shouldAutoFocus
            isRequired
          />
        </div>
      </fieldset>
    </form>
  </SheetModal>
</template>

<script lang="ts" setup>
import FormInput from "@/components/FormInput.vue";
import SheetHeader from "@/components/SheetHeader.vue";
import SheetModal from "@/components/SheetModal.vue";
import useAuthStore from "@/stores/useAuthStore";
import { Capacitor } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";

const emit = defineEmits<{
  close: [],
}>();

const {
  session,
} = storeToRefs(useAuthStore());

const isRequesting = ref(false);

const form = ref<{
  message: string | null,
}>({
  message: null,
});

const trimmedMessage = computed(() => form.value.message?.trim());

async function handleSubmit () {
  if (!trimmedMessage.value) return;

  isRequesting.value = true;

  const info = await Device.getInfo();
  const context = {
    platform: Capacitor.getPlatform(),
    model: info.model,
    operatingSystem: info.operatingSystem,
    osVersion: info.osVersion,
    manufacturer: info.manufacturer,
    webViewVersion: info.webViewVersion,
    appVersion: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA ?? null,
  };

  try {
    const response = await fetch("/api/feedback", {
      method: "POST",
      body: JSON.stringify({
        message: trimmedMessage.value,
        context,
      }),
      headers: {
        Authorization: `Bearer ${session.value!.access_token}`,
      },
    });

    if (!response.ok) {
      const {
        error,
      } = await response.json();
      throw new Error(error?.message || "Something went wrong");
    }

    emit("close");
  } catch (error) {
    alert((error as Error).message);
  }

  isRequesting.value = false;
}
</script>

<style lang="scss" scoped>
.FeedbackModalView__fieldset {
  display: grid;
  align-content: start;
  height: 100%;
}

.FeedbackModalView__fieldGrid {
  padding: 16px 16px calc(16px + var(--safeAreaInsetBottom));
}
</style>
